import { useEffect, useMemo, useState, useRef } from 'react';
import { Box, ButtonBase, Divider, Popover, Typography, Chip } from '@mui/material';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PublicIcon from '@mui/icons-material/Public';
import { db } from '../../lib/firebase';
import useParameters from '../../hooks/useParameters';
import { getCountryConfig, hasCountryConfig, resolveOrgCountry } from '../../utils/countryConfig';

const formatNumber = (value) => {
  if (value == null || value === '') return 'N/A';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return 'N/A';
  const [intPart, decPart] = num.toFixed(2).split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formattedInt},${decPart}`;
};

export default function ExchangeRatesPopover() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({});
  const { parametrosDB } = useParameters();

  // Base currencies relevant to the user's orgs. Each user org resolves against the
  // full params org list by value (organizacionUser snapshots can lack country).
  const userBases = useMemo(() => {
    const allOrgs = parametrosDB?.organizacion || [];
    const userOrgs = parametrosDB?.organizacionUser?.length ? parametrosDB.organizacionUser : allOrgs;
    const bases = new Set();
    for (const userOrg of userOrgs) {
      const liveOrg = allOrgs.find((o) => o.value === userOrg.value) || userOrg;
      const country = resolveOrgCountry(liveOrg);
      if (hasCountryConfig(country)) {
        bases.add(getCountryConfig(country).baseCurrency);
      }
    }
    return bases;
  }, [parametrosDB]);

  useEffect(() => {
    let isMounted = true;

    const fetchRates = async () => {
      const docRef = db.collection('params').doc('exchangeRates');
      const docSnap = await docRef.get();
      if (isMounted) {
        setExchangeRates(docSnap.data() || {});
      }
    };
    fetchRates();

    return () => {
      isMounted = false;
    };
  }, []);

  // params/exchangeRates is multi-base: one rate table per base currency
  // (e.g. { CLP: { UF, USD, EUR, fecha } }) — render one section per base.
  // Non-table keys (Timestamps carry toDate) are skipped.
  const allTables = Object.entries(exchangeRates)
    .filter(([, table]) => table && typeof table === 'object' && !table.toDate)
    .sort(([a], [b]) => a.localeCompare(b));

  // Show only the bases relevant to the user's orgs; if none resolve/match
  // (e.g. config not loaded), degrade to showing every table rather than nothing.
  const relevantTables = allTables.filter(([base]) => userBases.has(base));
  const baseTables = relevantTables.length > 0 ? relevantTables : allTables;

  const RATE_ORDER = ['USD', 'EUR', 'UF'];
  const rateKeys = (table) =>
    Object.keys(table)
      .filter((key) => typeof table[key] === 'number')
      .sort((a, b) => {
        const ia = RATE_ORDER.indexOf(a);
        const ib = RATE_ORDER.indexOf(b);
        return (
          (ia === -1 ? RATE_ORDER.length : ia) - (ib === -1 ? RATE_ORDER.length : ib) ||
          a.localeCompare(b)
        );
      });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    // Handle Firestore Timestamp
    if (dateValue?.toDate) {
      return dateValue.toDate().toLocaleDateString('es-CL');
    }
    // Handle ISO string - extract date portion directly to avoid timezone shift
    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      const [datePart] = dateValue.split('T');
      const [year, month, day] = datePart.split('-');
      return `${day}-${month}-${year}`;
    }
    // Handle regular Date
    const date = new Date(dateValue);
    return `${String(date.getUTCDate()).padStart(2, '0')}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${date.getUTCFullYear()}`;
  };

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <AttachMoneyOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{ sx: { width: 260, borderRadius: 1.5 } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <PublicIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography color="textPrimary" variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.95rem' }}>
              Tipos de Cambio
            </Typography>
          </Box>
          {baseTables.length === 0 && (
            <Typography color="textSecondary" variant="caption" sx={{ fontSize: '0.75rem' }}>
              Sin tipos de cambio disponibles
            </Typography>
          )}
        </Box>
        {baseTables.map(([base, table]) => (
          <Box key={base}>
            <Divider sx={{ my: 0 }} />
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography color="textSecondary" variant="caption" sx={{ fontSize: '0.75rem' }}>
                  Actualizado {formatDate(table.fecha)}
                </Typography>
                <Chip size="small" label={base} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
              {rateKeys(table).map((code) => (
                <Box
                  key={code}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
                >
                  <Typography variant="body2" color="textSecondary">
                    {code}/{base}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatNumber(table[code])}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Popover>
    </>
  );
}
